import * as React from "react";

import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import { Flex, Grid, Stack, Text } from "@mantine/core";

import {
  _CollectionType,
  PriceType,
  TokenDistributionType,
} from "apollo/graphql.generated";
import { SelectBox } from "components/sale/SelectBox";

export type MintDetails = {
  priceType: PriceType;
  distributionType: TokenDistributionType;
  isFreePrice: boolean;
};

type Props = {
  collectionType: _CollectionType;
  value: MintDetails;
  disableEnglishAuction?: boolean;
  onChange: (value: MintDetails) => void;
  hidden?: boolean;
};

export const PriceTypeInput: React.FC<Props> = ({
  onChange,
  value,
  disableEnglishAuction,
  collectionType,
}) => {
  const enableDutchAuctionsCreation = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_DUTCH_AUCTIONS_CREATION
  );
  const enableCreatorRewards = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_CREATOR_REWARDS
  );

  const onToggleMint = () =>
    onChange({
      distributionType: TokenDistributionType.Mint,
      priceType: PriceType.Fixed,
      isFreePrice: false,
    });
  const onTogglePriceFixed = () =>
    onChange({
      distributionType: TokenDistributionType.Mint,
      priceType: PriceType.Fixed,
      isFreePrice: false,
    });

  const onToggleDutchAuction = () =>
    onChange({
      distributionType: TokenDistributionType.Mint,
      priceType: PriceType.DutchAuction,
      isFreePrice: false,
    });

  const onToggleAuction = () =>
    onChange({
      distributionType: TokenDistributionType.Auction,
      priceType: PriceType.Fixed,
      isFreePrice: false,
    });

  const onToggleFreePrice = () =>
    onChange({
      distributionType: TokenDistributionType.Mint,
      priceType: PriceType.Fixed,
      isFreePrice: true,
    });

  const isAuction = value.distributionType === TokenDistributionType.Auction;
  const isPriceFixed = value.priceType === PriceType.Fixed;
  const isDutchAuction = value.priceType === PriceType.DutchAuction;
  const isFreePrice =
    value.priceType === PriceType.Fixed &&
    value.isFreePrice &&
    !!enableCreatorRewards;

  const showOnlyFixedPrice = enableCreatorRewards && isPriceFixed;

  return (
    <Stack spacing={8} hidden={!showOnlyFixedPrice}>
      {collectionType == _CollectionType.OneOfOne ? (
        <>
          <Text component="label" size="sm">
            Sale type
          </Text>
          <Flex
            justify="center"
            align="flex-start"
            direction={enableDutchAuctionsCreation ? "column" : "row"}
            gap={12}
          >
            <SelectBox
              checked={!isAuction && isPriceFixed && !isFreePrice}
              label="Mint"
              description="Sell this token for a fixed price that you set"
              onClick={onToggleMint}
            />
            {!disableEnglishAuction && !showOnlyFixedPrice && (
              <SelectBox
                checked={isAuction}
                label="Auction"
                description="Let people bid on this token in an auction with a
                        reserve price"
                onClick={onToggleAuction}
              />
            )}
            {enableDutchAuctionsCreation && !showOnlyFixedPrice && (
              <SelectBox
                checked={isDutchAuction}
                label="Dutch auction"
                description="Create a schedule of decreasing prices"
                onClick={onToggleDutchAuction}
              />
            )}
            {enableCreatorRewards && (
              <Grid.Col span={6}>
                <SelectBox
                  checked={isFreePrice}
                  label="Free"
                  description="Let collectors mint for free and earn 50% of all the mints"
                  onClick={onToggleFreePrice}
                />
              </Grid.Col>
            )}
          </Flex>
        </>
      ) : (
        <>
          <Text component="label" size="sm">
            Pricing model
          </Text>
          <Flex
            justify="center"
            align="flex-start"
            direction={
              enableDutchAuctionsCreation && enableCreatorRewards
                ? "column"
                : "row"
            }
            gap={12}
          >
            {enableCreatorRewards && (
              <SelectBox
                checked={isFreePrice}
                label="Free"
                description="Let collectors mint for free and earn 50% of all the mints"
                onClick={onToggleFreePrice}
              />
            )}
            <SelectBox
              checked={isPriceFixed && !isFreePrice}
              label="Fixed price"
              description="Sell this token for a fixed price that you set"
              onClick={onTogglePriceFixed}
            />
            {enableDutchAuctionsCreation && !showOnlyFixedPrice && (
              <SelectBox
                checked={isDutchAuction}
                label="Dutch auction"
                description="Create a schedule of decreasing prices"
                onClick={onToggleDutchAuction}
              />
            )}
          </Flex>
        </>
      )}
    </Stack>
  );
};
